import {
    type ComponentType,
    createContext,
    createElement,
    useContext,
} from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridColumnVariants } from "../columns/types";
import type { DataGridCellProps } from "./types";

/** The column's current variant, provided by the grid's cell wrapper. */
export const CellVariantContext = createContext<string | undefined>(undefined);

/** The chosen variant when the column offers it, else the column's default. */
export const getVariantValue = (
    variants: DataGridColumnVariants,
    chosen: string | undefined,
) =>
    chosen !== undefined &&
    variants.options.some((option) => option.value === chosen)
        ? chosen
        : variants.defaultValue;

interface VariantCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    renderer: ComponentType<DataGridCellProps<TData>>;
}

/** Renders `renderer` with the cell's `variant`. */
export const VariantCell = <TData extends RowData>({
    renderer,
    context,
    meta,
}: Readonly<VariantCellProps<TData>>) =>
    createElement(renderer, {
        context,
        meta,
        variant: useContext(CellVariantContext),
    });
